import { useRef, useState } from "react";
import type { SessionScope } from "../../auth/session-scope.js";

/**
 * Per-row state for a mutation that ONE `useMutation` drives across N rows.
 *
 * The mutation observer is LATEST-ONLY, which is the trap this exists to avoid:
 *
 *   - `useMutation().variables` holds only the most recent `mutate()` call, so a
 *     `variables`-derived pending flag disables just the last-acted row and
 *     RE-ENABLES an earlier one still in flight.
 *   - `useMutation().error` is latest-only for the same reason, so one row's
 *     failure renders against another row — or, when a later call succeeds, is
 *     dropped entirely.
 *   - per-call callbacks passed to `mutate(vars, { onSettled })` live on the
 *     observer's single `mutateOptions` and are overwritten by the next call, so
 *     the first request settling runs the second row's cleanup.
 *
 * So pending and error state are keyed maps, and cleanup binds to each call's own
 * `mutateAsync()` promise. Same shape as `useProjectionRetry` beside this file and
 * `usePhaseLockToggle` in packages/solve-feature, generalised because the Members
 * admin page needs it five times over.
 *
 * Options-level `onMutate`/`onSettled` would also see the right `variables` — they
 * are NOT latest-only. They are deliberately not used: the state belongs to the
 * page, not to the mutation, and five inline copies of it is what this replaces.
 */
export interface RowVariables {
  readonly userId: string;
  readonly scope: SessionScope;
}

export interface RowMutationController<TVars extends RowVariables> {
  readonly isPending: (userId: string) => boolean;
  readonly errorFor: (userId: string) => string | null;
  readonly run: (variables: TVars) => void;
}

interface RowMutationOptions<TVars extends RowVariables> {
  /** The session the ROW is being rendered for — the render fence. */
  readonly scope: SessionScope;
  readonly describeError: (error: unknown, variables: TVars) => string;
}

/* The entry key is the session PLUS the member, never the member alone.
   Two administrators of one tenant can replace one another in place without
   `/admin` unmounting (see auth/session-scope.ts). With a member-only key, the
   retired administrator's request settling would delete the live
   administrator's entry for that same member and unlock a row whose request is
   still in flight.

   A tuple through JSON, not a joined string: no separator can collide with a
   value, so no id needs escaping. `null` is a signed-out or claimless render,
   which addresses nothing — the same reading `sameSession` gives it. */
export function rowKey(scope: SessionScope, userId: string): string | null {
  if (scope === null) return null;
  return JSON.stringify([
    scope.tenantId,
    scope.actorUserId,
    scope.authSessionGeneration,
    userId,
  ]);
}

export function useRowMutation<TVars extends RowVariables>(
  mutation: { readonly mutateAsync: (variables: TVars) => Promise<unknown> },
  options: RowMutationOptions<TVars>,
): RowMutationController<TVars> {
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  /* The re-entrancy guard reads a REF, not the `pending` state. Two clicks in
     one tick both run before React re-renders, so a state-derived guard would
     let both through and fire two requests for one row — which is the
     out-of-order hazard the guard is here to prevent. The Set state exists for
     rendering; the ref is what decides. */
  const inFlight = useRef<Set<string>>(new Set());

  const run = (variables: TVars): void => {
    /* The WRITE fence: the key comes from the scope that SUBMITTED this call,
       so every later write addresses its own entry or nothing at all. */
    const key = rowKey(variables.scope, variables.userId);
    if (key === null || inFlight.current.has(key)) return;

    inFlight.current.add(key);
    setPending((prev) => new Set(prev).add(key));
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

    void mutation
      .mutateAsync(variables)
      /* The mutation's own `onError` still runs — it is an options-level
         callback and receives this call's variables. This catch exists so the
         row can say something, and so a rejection this hook created an observer
         for is not left unhandled. */
      .catch((error: unknown) => {
        setErrors((prev) =>
          new Map(prev).set(key, options.describeError(error, variables)),
        );
      })
      .finally(() => {
        inFlight.current.delete(key);
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      });
  };

  /* The RENDER fence: what may be DISPLAYED is asked of the CURRENT session, so
     a retired session's entry is simply not addressed rather than cleared. It
     stays until its request settles, which is what keeps the two fences from
     having to agree about timing. */
  const currentKey = (userId: string): string | null =>
    rowKey(options.scope, userId);

  return {
    isPending: (userId) => {
      const key = currentKey(userId);
      return key !== null && pending.has(key);
    },
    errorFor: (userId) => {
      const key = currentKey(userId);
      return key === null ? null : (errors.get(key) ?? null);
    },
    run,
  };
}
